// @ts-nocheck
import _ from 'lodash'
import validation from '.'

/**
 * NHS Number validation taken from the official specification. Only real NHS
 * Numbers will pass this function.
 *
 * https://www.datadictionary.nhs.uk/version2/data_dictionary/data_field_notes/n/nhs_number_de.asp
 *
 * @param {Number} digits the current digits entered by the user
 */
export function isValidNhsNumber(digits) {
  let isValid = false
  let message = 'NHS Number must be 10 digits long.'

  if (!digits)
    return {
      isValid,
      message
    }

  const number = digits.toString()

  if (number.length === 10) {
    let total = 0

    let i = 0
    for (i = 0; i <= 8; i++) {
      const digit = number[i]
      const factor = 10 - i
      total += digit * factor
    }

    let checkDigit = 11 - (total % 11)

    if (checkDigit === 11) {
      checkDigit = 0
    }

    if (checkDigit.toString() === number[9]) {
      isValid = true
    } else {
      message = 'Invalid NHS Number.'
    }
  }

  return {
    isValid,
    message
  }
}

/**
 * Core validation function for  forms / fields. Checks the current value of
 * the field (and the values of its related keys) against the field's
 * validation properties, such as whether it is a `required` field. Runs the
 * values against the validation functions found in `./index.js`.
 *
 * @param {String} key the key for the current field
 * @param {Any} value the value for the current field
 * @param {Array} relatedValues the form values for all the current field's
 * related keys
 * @param {Object} validationProperties validation properties for the current
 * field, such as `required` and `relatedKeys`
 */
export function validateSingleField({
  key,
  value,
  relatedValues,
  validationProperties
}) {
  if (
    validationProperties.required &&
    ((!value && value !== false && value !== 0) ||
      (_.isArray(value) && !value.length))
  ) {
    const label = validationProperties.label || _.capitalize(key)
    return {
      validationError: `${label} is required`
    }
  }
  if (validationProperties.validationTypes.length) {
    for (const type of validationProperties.validationTypes) {
      if (!value) {
        continue
      }

      const validateFunction = _.isObject(type)
        ? validation[type.typeKey]
        : validation[type]
      const { isValid, message } = validateFunction({
        label: validationProperties.label,
        value,
        relatedValues
      })
      if (!isValid) {
        return {
          validationError: message
        }
      }
    }
  }
  return {}
}

// Performs `validateSingleField` for all appropriate fields to collect errors
export function validateForm(formFields, fieldsToValidate) {
  return new Promise((resolve, reject) => {
    let errors = {}

    for (const key of Object.keys(fieldsToValidate)) {
      const validationProperties = fieldsToValidate[key]
      const value = _.get(formFields, key)

      // Object validation types are objects not strings, so related keys can be passed in as part of the object.
      const objectValidationTypes =
        validationProperties &&
        _.filter(validationProperties.validationTypes, (type) => {
          return _.isObject(type)
        })

      const relatedKeys =
        validationProperties &&
        _.compact(
          _.concat(
            validationProperties.relatedKeys,
            _.flatten(objectValidationTypes.map((type) => type.relatedKeys))
          )
        )
      const relatedValues =
        relatedKeys && relatedKeys.length
          ? relatedKeys.map((key) => _.get(formFields, key))
          : null

      // don't validate hidden dependent fields
      if (validationProperties.dependsOn) {
        if (
          isDependentFieldHidden(formFields, validationProperties.dependsOn)
        ) {
          continue
        }
      }

      const result = validateSingleField({
        key,
        value,
        relatedValues,
        validationProperties
      })

      if (result.validationError) {
        errors = {
          ...errors,
          [key]: result.validationError
        }
      }
    }

    if (Object.keys(errors).length) {
      reject({ errors })
    } else {
      resolve('Success')
    }
  })
}

/**
 * Important function that dictates whether or not to display dependent fields.
 * Should be used throughout the app whenever this state needs to be determined.
 *
 * @param {Object} fields the fields in `state.forms[formId].fields`
 * @param {*} dependsOn the key and value that are required to be present in
 * `fields` in order for the dependent field to be displayed
 */
export function isDependentFieldHidden(fields, dependsOn) {
  if (_.has(dependsOn, 'value')) {
    // if value is supplied, hidden if values don't match
    if (dependsOn.value !== _.get(fields, dependsOn.key)) return true
  } else if (
    !_.has(fields, dependsOn?.key) ||
    _.get(fields, dependsOn?.key) === undefined
  ) {
    // otherwise, hidden if specified field doesn't exist
    return true
  }
  return false
}
